import styled from 'styled-components'
import { SpaceProps, space, WidthProps, width } from 'styled-system'

import { Flex } from '@/atoms/layout'
import { filterSpaceProps } from '@/helpers/styled-props'

export const AppWrapper = styled.div`
  display: flex;
  min-height: 100dvh;
  height: 100dvh;
  position: relative;
  /* Specific padding for devices that have a safe area inset */
  padding-bottom: env(safe-area-inset-bottom);
`

export const ContentWrapper = styled.main<{ $sideMenuLocked?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: ${({ theme, $sideMenuLocked }) =>
    $sideMenuLocked ? `calc(100vw - ${theme.drawerWidthOpen}px)` : `calc(100vw - ${theme.drawerWidthClosed}px)`};
  width: 100%;
  margin-left: ${({ theme, $sideMenuLocked }) => ($sideMenuLocked ? `${theme.drawerWidthOpen}px` : `${theme.drawerWidthClosed}px`)};
  margin-top: ${({ theme }) => `${theme.drawerWidthClosed}px`};
  box-sizing: border-box;
  padding-bottom: env(safe-area-inset-bottom);
`

export const ErrorWrapper = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
`

export const UserInfoWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin: 0 ${({ theme }) => theme.space.S16};
`

export const User = styled.p`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  margin: 0;
`

export const UserName = styled(User)`
  color: ${({ theme }) => theme.colors.heading};
  font-size: 1rem;
  line-height: 1.5em;
`

export const UserEntity = styled(User)`
  color: ${({ theme }) => theme.colors.grey};
  font-size: 0.875rem;
  line-height: 1.46429em;
`

// Header

export const HeaderContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${({ theme }) => theme.space.S8} 0;
`

export const HeaderWrapper = styled.header`
  position: fixed;
  width: 100%;
  min-height: 64px;
  top: 0;
  z-index: 102;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: solid 1px ${({ theme }) => theme.colors.bgVioletLight};
  box-shadow: none;
  padding-right: ${({ theme }) => theme.space.S40};

  ${({ theme }) => theme.mediaQueries.mobile} {
    padding-right: ${({ theme }) => theme.space.S8};
  }
`

// DropDown
export const DropdownMenu = styled.div`
  position: relative;
`

export const NavButton = styled.button<{ $isHovered?: boolean; $isPublic?: boolean; $hasOneOption?: boolean }>`
  color: ${({ theme, $isPublic }) => ($isPublic ? theme.colors.white : theme.colors.label)};
  font-family: ${({ theme }) => theme.fonts.textFont};
  font-weight: ${({ theme }) => theme.fonts.fontWeightMedium};
  font-size: ${({ theme, $isPublic }) => ($isPublic ? theme.fonts.fontSize18 : theme.fonts.fontSize12)};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.space.S12};
  border: none;
  background-color: ${({ $isHovered, $isPublic, theme }) => ($isHovered ? ($isPublic ? 'transparent' : theme.colors.bgLight) : 'transparent')};
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  white-space: nowrap;
  width: auto;
  min-width: 40px;
  min-height: 44px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme, $isPublic, $hasOneOption }) => ($isPublic || $hasOneOption ? 'transparent' : theme.colors.bgLight)};
  }

  svg:not(:root) {
    overflow: unset;
  }

  svg {
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);

    path {
      fill: ${({ theme, $isPublic }) => $isPublic && theme.colors.white};
    }
  }

  svg.rotate {
    transform: rotate(180deg);
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    padding: ${({ theme }) => theme.space.S4};
  }
`

export const DropdownMenuList = styled.ul.withConfig({
  shouldForwardProp: filterSpaceProps('$alignRight', 'width'),
})<WidthProps & SpaceProps & { $alignRight?: boolean }>`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12);
  border: 1px solid rgb(235, 237, 248);
  border-radius: 4px;
  padding: ${({ theme }) => theme.space.S8} 0;
  margin: 0;
  right: 0;
  left: ${({ $alignRight }) => ($alignRight ? 'unset' : 0)};
  width: 100%;

  ${width}
  ${space}
`

export const MenuItemWrapper = styled.li<{ $isPublic?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.space.S12};
  position: relative;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  white-space: nowrap;
  width: auto;
  min-width: 40px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.label};
  font-family: ${({ theme }) => theme.fonts.textFont};
  font-weight: ${({ theme }) => theme.fonts.fontWeightMedium};
  font-size: ${({ theme, $isPublic }) => ($isPublic ? theme.fonts.fontSize18 : theme.fonts.fontSize12)};
  text-transform: uppercase;

  svg:not(:root) {
    overflow: unset;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgLight};
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    padding: ${({ theme }) => theme.space.S8};
  }
`

export const ItemText = styled.div<{ $hasIconLeft?: boolean; $hasIconRight?: boolean }>`
  margin-left: ${({ theme, $hasIconLeft }) => ($hasIconLeft ? theme.space.S8 : 0)};
  margin-right: ${({ theme, $hasIconRight }) => ($hasIconRight ? theme.space.S4 : 0)};
  text-align: ${({ $hasIconLeft, $hasIconRight }) => ($hasIconLeft || $hasIconRight ? 'left' : 'center')};
  width: 100%;

  ${({ theme }) => theme.mediaQueries.mobile} {
    margin-left: ${({ theme, $hasIconLeft }) => ($hasIconLeft ? theme.space.S4 : 0)};
    margin-right: 0;
  }
`

export const Contact = styled(Flex)`
  align-items: center;
  margin-top: ${({ theme }) => theme.space.S8};
`
